import { useCallback, useMemo } from 'react';
import { ProcessingRuleItem, ServiceConfig } from '@common-fe/common-fe';

import { TriggerCategory } from '@/modules/HealthPlan/HealthPlan.types.ts';
import { useHealthPlanNameStore } from '@/modules/HealthPlan/HealthPlanSetup/BasicProperties/HealthPlanName/stores';
import { replaceRxWithPharmacyType } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/AddServicePaymentModal/queries/useServiceType.query.ts';

import { CARD_PURSE_LOCK, CARD_PURSE_UNLOCK } from '../TierItem/tierItem.types';

export interface TierField {
  id: string;
  name: string;
  payoutDefinition: string;
  payoutDefinitionName: string;
  familyDeductible?: string;
  individualDeductible?: string;
  isSendTriggerNotification?: boolean;
  triggers: string[];
  aggregateTriggerName?: string;
  embeddedTriggerName?: string;
  displayName?: string;
  planName?: string;
  payoutRuleType?: string;
  lockCardPurse?: string;
  planIdsForCardPurseLock?: string[];
  planNamesForCardPurseLock?: string[];
  nextTierId?: number | null;
}

export default (currentRule?: ProcessingRuleItem, serviceConfigs?: ServiceConfig[], isLoading?: boolean) => {
  const healthPlanTypes = useHealthPlanNameStore((state) => state.state.healthPlanTypes);
  const updatedPlanTypes = useMemo(() => replaceRxWithPharmacyType(healthPlanTypes), [healthPlanTypes]);

  const fieldsValues = useMemo<TierField[]>(() => {
    if (isLoading || !currentRule?.tiers?.length) return [];

    return currentRule.tiers.map((el) => {
      const tierTriggers = el.tierTriggers || [];
      const aggregateTriggerName = tierTriggers.find(
        (t) => t.triggerCategoryType === TriggerCategory.FAMILY
      )?.name;
      const embeddedTriggerName = tierTriggers.find(
        (t) => t.triggerCategoryType === TriggerCategory.EMBEDDED
      )?.name;

      let triggers = el?.triggers || [];

      const tierTriggerNames = tierTriggers.map((t) => t.name || '');
      if (tierTriggerNames.length) {
        triggers = tierTriggerNames;
      }

      let lockCardPurse: string | undefined;
      if (el.nextTierId !== null) {
        lockCardPurse = el.isLockPurse ? CARD_PURSE_LOCK : CARD_PURSE_UNLOCK;
      }

      return {
        id: el.id,
        name: el.name,
        payoutDefinition: el.payoutDefinition.id,
        payoutDefinitionName: el.payoutDefinition.name,
        isSendTriggerNotification: el.isSendTriggerNotification,
        triggers,
        aggregateTriggerName,
        embeddedTriggerName,
        displayName: el.displayName,
        planName: el.planName,
        payoutRuleType: (el as unknown as { payoutRuleType?: string }).payoutRuleType,
        lockCardPurse,
        planIdsForCardPurseLock: el.planIdsForCardPurseLock || [],
        planNamesForCardPurseLock: el.planNamesForCardPurseLock || [],
        nextTierId: el.nextTierId,
      };
    });
  }, [currentRule, isLoading]);

  const getServiceConfigsByTierId = useCallback(
    (tierId: string) =>
      serviceConfigs
        ?.filter((item) => item.processingTierId === tierId)
        .map((item) => ({
          ...item,
          isDangerMode: !updatedPlanTypes?.includes(item.serviceType || ''),
        })) || [],
    [serviceConfigs, updatedPlanTypes]
  );

  const getFieldValuesById = useCallback(
    (id?: string) => fieldsValues.find((field) => field.id === id),
    [fieldsValues]
  );

  return {
    getFieldValuesById,
    getServiceConfigsByTierId,
    fieldsValues,
  };
};
